import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Link } from "react-router-dom";
import { Users, Warning, CalendarCheck, PaperPlaneTilt, ArrowRight, CurrencyInr, Receipt, HourglassMedium } from "@phosphor-icons/react";
import UsageMeter from "@/components/app/UsageMeter";

const inr = (n) => `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatCard = ({ label, value, accent, icon: Icon, testid }) => (
  <div data-testid={testid} className="nb-card nb-card-hover p-5 flex flex-col gap-3 h-full" style={{ background: accent }}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold tracking-[0.2em] uppercase">{label}</span>
      <div className="w-9 h-9 bg-white border-2 border-black rounded-md flex items-center justify-center">
        <Icon size={18} weight="bold" />
      </div>
    </div>
    <div className="font-heading font-black text-4xl tracking-tighter">{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [payStats, setPayStats] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, r, p] = await Promise.all([api.get("/stats"), api.get("/reminders"), api.get("/stats/payments")]);
      setStats(s.data); setReminders(r.data); setPayStats(p.data);
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Overview</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="stats-grid">
        <StatCard label="Total Customers" value={stats?.total_customers ?? "—"} accent="#FFFFFF" icon={Users} testid="stat-total-customers" />
        <StatCard label="Overdue Services" value={stats?.overdue ?? "—"} accent="#FF6B6B" icon={Warning} testid="stat-overdue" />
        <StatCard label="Due in 7 Days" value={stats?.upcoming_7_days ?? "—"} accent="#FFD166" icon={CalendarCheck} testid="stat-upcoming" />
        <StatCard label="Sent This Month" value={stats?.sent_this_month ?? "—"} accent="#4ECDC4" icon={PaperPlaneTilt} testid="stat-sent" />
      </div>

      <UsageMeter />

      {payStats?.has_invoices && (
        <>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Payment collection</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mt-1">Money in motion</h2>
            </div>
            <Link to="/invoices" data-testid="see-all-invoices" className="text-xs font-bold uppercase tracking-wider underline">See all invoices <ArrowRight size={12} weight="bold" className="inline" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pay-stats-grid">
            <StatCard label="Total Outstanding" value={inr(payStats.total_outstanding)} accent="#FFFFFF" icon={CurrencyInr} testid="stat-total-outstanding" />
            <StatCard label="Overdue Amount" value={inr(payStats.overdue_amount)} accent="#FF6B6B" icon={Warning} testid="stat-overdue-amount" />
            <StatCard label="Due Today" value={inr(payStats.due_today)} accent="#FFD166" icon={HourglassMedium} testid="stat-due-today" />
            <StatCard label="Due This Week" value={inr(payStats.due_this_week)} accent="#4ECDC4" icon={CalendarCheck} testid="stat-due-week" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="nb-card p-5" data-testid="stat-pending">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Pending payments</p>
              <p className="font-heading text-3xl font-black mt-1">{payStats.pending_payments}</p>
              <p className="text-xs font-semibold text-[#4A4A4A] mt-1">across {payStats.overdue_customers} overdue customer{payStats.overdue_customers === 1 ? "" : "s"}</p>
            </div>
            <div className="nb-card p-5">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Recently paid</p>
              {(payStats.recent_payments || []).length === 0 ? <p className="text-sm text-[#4A4A4A] font-semibold">No recent payments.</p> :
                <ul className="space-y-1.5">
                  {payStats.recent_payments.slice(0, 4).map((p) => (
                    <li key={p.payment_id} className="flex justify-between text-sm font-semibold">
                      <span className="truncate">{p.payment_date} · {p.method?.toUpperCase()}</span>
                      <span className="font-mono text-[#06D6A0] font-bold">{inr(p.amount)}</span>
                    </li>
                  ))}
                </ul>}
            </div>
          </div>
        </>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-black">Overdue Services</h2>
            <Link to="/reminders" data-testid="see-all-overdue" className="text-xs font-bold uppercase tracking-wider underline">See all <ArrowRight size={12} weight="bold" className="inline" /></Link>
          </div>
          <ReminderList items={reminders?.overdue || []} accent="#FF6B6B" empty="No overdue customers." />
        </div>
        <div className="nb-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-black">Due This Week</h2>
            <Link to="/reminders" data-testid="see-all-week" className="text-xs font-bold uppercase tracking-wider underline">See all <ArrowRight size={12} weight="bold" className="inline" /></Link>
          </div>
          <ReminderList items={[...(reminders?.due_today || []), ...(reminders?.this_week || [])]} accent="#FFD166" empty="Nothing due this week." />
        </div>
      </div>
    </div>
  );
}

function ReminderList({ items, accent, empty }) {
  if (!items.length) return <p className="text-sm font-semibold text-[#4A4A4A] py-6 text-center">{empty}</p>;
  return (
    <ul className="space-y-2.5">
      {items.slice(0, 6).map((c) => (
        <li key={c.customer_id} className="flex items-center justify-between border-2 border-black rounded-md px-3 py-2.5">
          <div className="min-w-0">
            <p className="font-bold truncate">{c.name}</p>
            <p className="text-xs text-[#4A4A4A] font-semibold truncate">{c.business_type} • {c.phone}</p>
          </div>
          <span className="ml-3 flex-shrink-0 text-xs font-black px-2.5 py-1 border-2 border-black rounded-full" style={{ background: accent }}>
            {c.days_until < 0 ? `${Math.abs(c.days_until)}d late` : c.days_until === 0 ? "Today" : `${c.days_until}d`}
          </span>
        </li>
      ))}
    </ul>
  );
}
